import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef, ForwardedRef } from "react";
import axios from "axios";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
import { useStripe, useElements, PaymentElement, Elements } from "@stripe/react-stripe-js";
import { StripePaymentElementOptions, loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import style from "./Dashboard.module.scss";
import { SecondaryBtn } from "../../common/Payment/SecondaryBtn";
import { InfoBtn } from "../../common/Payment/InfoBtn";
import { InfoCard } from "../../common/Payment/InfoCard";

type Props = {
  code: string;
  locale: string;
  handleClose: () => void;
  country_code: string;
  completeMethod: () => void;
  currentPlan: any;
};

type CompanyInfoType = {
  company_name: string;
  workspace_name: string;
  address: {
    city: string;
    country: string;
    line1: string;
    line2: string;
    state: string;
    postal_code: string;
    prefecture: string;
    street: string;
    building: string;
  };
  billing_email: string;
};

const RegistCard = ({ code, locale, handleClose, country_code, completeMethod, currentPlan }: Props) => {
  const { t } = useTranslation();
  const ref = useRef<HandlerType>(null);

  const [clientSecret, setClientSecret] = useState(null);
  const [billingInfo, setBillingInfo] = useState<CompanyInfoType>();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [card, setCard] = useState(null);
  const [displayFormFlg, setDisplayFormFlg] = useState(true);
  const stripePromise = loadStripe(process.env.VITE_STRIPE_API_KEY);

  const getClientSecret = async () => {
    const result = await axios.get(`/${code}/api_cards/client_secret`).catch((error) => {
      return null;
    });

    if (result) {
      return result.data.client_secret;
    }
  };

  const getBillings = async () => {
    const result = await axios.get(`/${code}/settings/billing/retrieve_billing_linked_card`).catch((error) => {
      return null;
    });

    if (result) {
      return result.data;
    }
  };

  const getCard = async () => {
    const result = await axios
      .get(`/${code}/api_cards/default_card`, {
        params: {
          default_only: true,
        },
      })
      .catch((error) => {
        return null;
      });

    if (result) {
      return result.data;
    }
  };

  const init = async () => {
    const [clientSecret, billingInfo, card] = await Promise.all([getClientSecret(), getBillings(), getCard()]);
    setClientSecret(clientSecret);
    setBillingInfo(billingInfo);
    if (card && card.id) {
      setCard(card);
      setDisplayFormFlg(false);
    }
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <>
      <div className={style.paymentMethodChangeModalTitle} style={{ whiteSpace: "nowrap" }}>
        {t("DashboardView.支払い方法の変更")}
      </div>
      <div className={style.paymentMethodChangeModalContent}>
        {t("DashboardView.支払い方法の変更が完了すると、次回のお支払い分より変更した方法にてお支払いが行われます。")}
      </div>

      {!displayFormFlg ? (
        <div className="mt-3 mb-4">
          <InfoCard
            card={card}
            paymentMethod="card"
            currentPlan={currentPlan}
            changeFunc={() => setDisplayFormFlg(true)}
          />
        </div>
      ) : (
        <div className="mt-5 mb-4">
          {clientSecret && billingInfo && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                locale: locale,
                appearance: {
                  labels: "floating",
                  theme: "none",
                  rules: {
                    ".Input": {
                      border: "1px solid #cacaca",
                      padding: "10px 16px",
                      fontSize: "14px",
                    },
                    ".Label--resting": {
                      fontSize: "14px",
                      color: "#cacaca",
                    },
                    ".Label--floating": {
                      fontSize: "12px",
                      color: "#6c757d",
                    },
                  },
                  variables: {
                    colorBackground: "#ffffff",
                    colorText: "#30313d",
                    colorDanger: "#ff4958",
                    fontFamily: "Avenir",
                    spacingUnit: "2px",
                    borderRadius: "12px",
                    gridRowSpacing: "16px",
                    colorTextPlaceholder: "#cacaca",
                  },
                },
              }}
            >
              <RegistCardElement
                ref={ref}
                code={code}
                billinginfo={billingInfo}
                country_code={country_code}
                setIsLoading={setIsLoading}
                completeMethod={completeMethod}
                setErrorMessage={setErrorMessage}
              />
            </Elements>
          )}
        </div>
      )}
      {errorMessage && (
        <div className={style.paymentMethodErr}>
          <div>{errorMessage}</div>
        </div>
      )}
      <div className={style.paymentMethodChangeModalButtonWrap}>
        <SecondaryBtn
          text={t("DashboardView.キャンセル")}
          disabled={false}
          className="test-force-upgrade"
          submit={() => handleClose()}
          style={{ color: "#222222", maxWidth: "260px", minWidth: "260px", width: "260px" }}
        />
        <InfoBtn
          text={t("DashboardView.変更する")}
          loading={isLoading}
          className="ml-auto test-force-upgrade"
          submit={() => ref.current?.updateCard()}
          style={{ maxWidth: "260px", minWidth: "260px", width: "260px" }}
        />
      </div>
    </>
  );
};

type RegisterCardElementProps = {
  code: string;
  billinginfo: CompanyInfoType;
  country_code: string;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  completeMethod: () => void;
};

export type HandlerType = {
  updateCard: () => void;
};

const RegistCardElement = forwardRef(
  (
    { code, billinginfo, country_code, setIsLoading, completeMethod, setErrorMessage }: RegisterCardElementProps,
    ref: ForwardedRef<HandlerType>,
  ) => {
    const stripe = useStripe();
    const elements = useElements();

    useImperativeHandle(ref, () => ({
      updateCard,
    }));

    const updateDefaultCard = async (token: string) => {
      await axios
        .post(`/${code}/api_cards/change_default`, {
          token_id: token,
        })
        .then(() => {
          return;
        })
        .catch((_) => {
          return;
        });
    };

    const updateCard = async () => {
      // Stripe.jsがまだロードされていない場合
      if (!stripe || !elements) return;
      setIsLoading(true);
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/${code}/api_cards/register_card_with_three_d_secure`,
          payment_method_data: {
            billing_details: {
              address: billinginfo.address,
            },
          },
        },
        redirect: "if_required",
      });

      if (result.setupIntent && result.setupIntent.payment_method) {
        await updateDefaultCard(result.setupIntent.payment_method as string);
        completeMethod();
      } else if (result.error) {
        setErrorMessage(result.error.message || "カード情報の登録に失敗しました");
      }
      setIsLoading(false);
    };

    const paymentElementOptions: StripePaymentElementOptions = {
      fields: { billingDetails: { address: "never" } },
      paymentMethodOrder: ["card"],
      wallets: {
        applePay: "never",
        googlePay: "never",
      },
    };

    return <PaymentElement options={paymentElementOptions} />;
  },
);

export default RegistCard;
